import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Handshake, Search, Filter, Eye, Mail, Phone, Globe, Building, 
  Loader2, CheckCircle, Clock, XCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSponsors = () => {
  const { getAuthAxios, fetchDashboard } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const tierLabels = {
    title: "Title Sponsor",
    gold: "Gold Sponsor",
    silver: "Silver Sponsor",
    bronze: "Bronze Sponsor",
    hydration: "Hydration Partner",
    venue: "Venue Partner",
    custom: "Custom Package"
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    contacted: "bg-yellow-100 text-yellow-700",
    negotiating: "bg-purple-100 text-purple-700",
    confirmed: "bg-green-100 text-green-700",
    declined: "bg-red-100 text-red-700"
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  useEffect(() => {
    filterInquiries();
  }, [inquiries, searchTerm, statusFilter]);

  const fetchInquiries = async () => {
    try {
      const authAxios = getAuthAxios();
      const response = await authAxios.get(`${API}/admin/sponsor-inquiries`);
      setInquiries(response.data.inquiries || []);
    } catch (error) {
      toast.error("Failed to fetch sponsor inquiries");
    } finally {
      setLoading(false);
    }
  };

  const filterInquiries = () => {
    let items = [...inquiries];
    
    if (statusFilter !== "all") {
      items = items.filter(i => i.status === statusFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(i => 
        i.company_name?.toLowerCase().includes(term) ||
        i.contact_person?.toLowerCase().includes(term) ||
        i.email?.toLowerCase().includes(term)
      );
    }
    
    setFilteredInquiries(items);
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const authAxios = getAuthAxios();
      await authAxios.put(`${API}/admin/sponsor-inquiries/${id}/status?status=${newStatus}`);
      toast.success("Status updated");
      fetchInquiries();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div data-testid="admin-sponsors">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Sponsor Inquiries</h1>
        <p className="text-[#4B5563]">Manage sponsorship and partnership requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {["new", "contacted", "negotiating", "confirmed", "declined"].map((status) => {
          const count = inquiries.filter(i => i.status === status).length;
          const icons = {
            new: Clock,
            contacted: Mail,
            negotiating: Handshake,
            confirmed: CheckCircle,
            declined: XCircle
          };
          const Icon = icons[status];
          return (
            <Card key={status} className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter(status)}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusColors[status]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xl font-bold">{count}</p>
                  <p className="text-xs text-[#4B5563] capitalize">{status}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input 
            placeholder="Search by company, contact, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="negotiating">Negotiating</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF3B30]" />
        </div>
      ) : filteredInquiries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Handshake className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Inquiries Found</h3>
            <p className="text-[#4B5563]">
              {searchTerm || statusFilter !== "all" ? "Try adjusting your filters" : "No sponsor inquiries yet"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInquiries.map((inquiry) => (
            <Card key={inquiry.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Building className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A0A0A]">{inquiry.company_name}</h3>
                      <p className="text-sm text-[#4B5563]">{inquiry.contact_person}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[inquiry.status]}`}>
                    {inquiry.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <p className="text-sm flex items-center gap-2 text-[#4B5563]">
                    <Mail className="w-4 h-4" />
                    {inquiry.email}
                  </p>
                  <p className="text-sm flex items-center gap-2 text-[#4B5563]">
                    <Phone className="w-4 h-4" />
                    {inquiry.phone}
                  </p>
                  {inquiry.website && (
                    <p className="text-sm flex items-center gap-2 text-[#4B5563]">
                      <Globe className="w-4 h-4" />
                      <a href={inquiry.website} target="_blank" rel="noopener noreferrer" className="text-[#007AFF] hover:underline truncate">
                        {inquiry.website}
                      </a>
                    </p>
                  )}
                </div>

                <div className="bg-[#FAFAFA] rounded-lg p-3 mb-4">
                  <p className="text-xs text-[#4B5563]">Interested In</p>
                  <p className="font-medium text-[#007AFF]">{tierLabels[inquiry.sponsorship_tier] || inquiry.sponsorship_tier}</p>
                </div>

                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => { setSelectedInquiry(inquiry); setViewDialogOpen(true); }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  <Select value={inquiry.status} onValueChange={(v) => updateStatus(inquiry.id, v)}>
                    <SelectTrigger className="flex-1 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="negotiating">Negotiating</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Sponsor Inquiry Details</DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Building className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedInquiry.company_name}</h3>
                  <p className="text-[#4B5563]">{selectedInquiry.contact_person}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#4B5563]">Email</p>
                  <a href={`mailto:${selectedInquiry.email}`} className="text-[#007AFF] hover:underline">
                    {selectedInquiry.email}
                  </a>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Phone</p>
                  <a href={`tel:${selectedInquiry.phone}`} className="text-[#007AFF] hover:underline">
                    {selectedInquiry.phone}
                  </a>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Sponsorship Tier</p>
                  <p className="font-medium">{tierLabels[selectedInquiry.sponsorship_tier]}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[selectedInquiry.status]}`}>
                    {selectedInquiry.status}
                  </span>
                </div>
              </div>
              
              {selectedInquiry.website && (
                <div>
                  <p className="text-xs text-[#4B5563]">Website</p>
                  <a href={selectedInquiry.website} target="_blank" rel="noopener noreferrer" className="text-[#007AFF] hover:underline">
                    {selectedInquiry.website}
                  </a>
                </div>
              )}
              
              {selectedInquiry.message && (
                <div>
                  <p className="text-xs text-[#4B5563]">Message</p>
                  <p className="text-[#0A0A0A] bg-[#FAFAFA] p-3 rounded-lg mt-1">{selectedInquiry.message}</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-4 border-t">
                <a href={`mailto:${selectedInquiry.email}`} className="flex-1">
                  <Button className="w-full btn-primary">
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                </a>
                <a href={`tel:${selectedInquiry.phone}`}>
                  <Button variant="outline">
                    <Phone className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSponsors;
